import { forwardRef, type AnchorHTMLAttributes } from 'react';
import { Icon } from './Icon.js';
import { VisuallyHidden } from './VisuallyHidden.js';
import './Link.css';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Shows a small arrow glyph and adds a screen-reader-only "(opens in new
   * tab)" disclosure, and sets target/rel safely — never an icon with no
   * text equivalent (a11y.md §2, never colour/icon-only). */
  external?: boolean;
}

/** A real `<a>`, restyled. `external` handles `target`/`rel` and the
 * disclosure text itself, so callers can't forget `rel="noopener noreferrer"`
 * or ship an icon with no accessible text equivalent. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { external = false, children, className, ...rest },
  ref
) {
  return (
    <a
      ref={ref}
      className={['dpr-link', className].filter(Boolean).join(' ')}
      target={external ? '_blank' : rest.target}
      rel={external ? 'noopener noreferrer' : rest.rel}
      {...rest}
    >
      {children}
      {external ? (
        <>
          <span className="dpr-link__external-icon" aria-hidden="true">
            <Icon name="external" size={14} />
          </span>
          <VisuallyHidden> (opens in new tab)</VisuallyHidden>
        </>
      ) : null}
    </a>
  );
});
